/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useMemo } from "react";
import type { GetProfessionalRegistrationLookupsResponse } from "@/domain";
import { useAuthenticatedProfessionalsApi } from "@/api";
import { useUserContext } from "./user-context";

// `null` = outside provider; `undefined` = inside provider, data not loaded yet
const LookupsContext = createContext<
  GetProfessionalRegistrationLookupsResponse | undefined | null
>(null);

interface LookupsProviderProps {
  children: React.ReactNode;
}

export const LookupsProvider = ({ children }: LookupsProviderProps) => {
  const { userType, isRegistrationRequired } = useUserContext();

  // Professional API
  const api = useAuthenticatedProfessionalsApi();
  const useGetProfessionalRegistrationLookupsQuery = useMemo(
    () => api.useGetProfessionalRegistrationLookupsQuery,
    [api]
  );

  // Fetch for registered professionals and for users still completing registration
  // (userType is intentionally unset while registrationRequired is true).
  const { data: lookups } = useGetProfessionalRegistrationLookupsQuery(
    {},
    {
      skip: !isRegistrationRequired && userType !== "professional",
    }
  );

  return (
    <LookupsContext.Provider value={lookups}>
      {children}
    </LookupsContext.Provider>
  );
};

// ============================================================================
// LOOKUPS CONTEXT HOOK
// ============================================================================

export const useLookupsContext = () => {
  const context = useContext(LookupsContext);

  if (context === null) {
    throw new Error("useLookupsContext must be used within a LookupsProvider");
  }

  return context;
};

// ============================================================================
// CONVENIENCE HOOKS
// ============================================================================

// Specialty Categories
export const useSpecialtyCategoriesFromLookupContext = () => {
  const lookups = useLookupsContext();
  return lookups?.specialtyCategories ?? [];
};

// Specialties
export const useSpecialtiesFromLookupContext = () => {
  const lookups = useLookupsContext();
  return lookups?.specialties ?? [];
};

// Skill Categories
export const useSkillCategoriesFromLookupContext = () => {
  const lookups = useLookupsContext();
  return lookups?.skillCategories ?? [];
};

// Skills
export const useSkillsFromLookupContext = () => {
  const lookups = useLookupsContext();
  return lookups?.skills ?? [];
};

// Professional Tiers
export const useProfessionalTiersFromLookupContext = () => {
  const lookups = useLookupsContext();
  return lookups?.professionalTiers ?? [];
};

// Experience Levels
export const useExperienceLevelsFromLookupContext = () => {
  const lookups = useLookupsContext();
  return lookups?.experienceLevels ?? [];
};

// Certificates
export const useCertificatesFromLookupContext = () => {
  const lookups = useLookupsContext();
  return lookups?.certificates ?? [];
};
